import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { api, authStore } from '../lib/api';
import type {
  AnswerElement, AnswerNode, ResponseDetail, ResponseFileRef,
} from '../lib/types';
import { Badge, Button, IconButton, Spinner } from './ui';
import { Icon, type IconName } from './icons';
import { relative } from '../pages/FormList';

/**
 * One response, read the way it was answered.
 *
 * The old drawer listed `questionRef: value` pairs straight from the document. That is
 * what the API returns and it is unreadable: `q7: "opt_2"` where the reader needs "How did
 * you hear about us? — A colleague". Worse, a form with pages, repeating groups and
 * matrices came out as one flat list, so nothing said which date of birth went with which
 * dependant.
 *
 * So the server resolves the document against the version it was answered against and this
 * draws the result: pages keep their headings, choices show what was on offer as well as
 * what was picked, repeating groups stay grouped, and questions nobody answered are shown
 * as unanswered rather than omitted — an absent question and an empty one are different
 * facts, and only one of them is a data problem.
 *
 * Used by the inbox and by the per-form Responses tab, so there is one answer view to fix
 * rather than two that drift.
 */
export function ResponseView({
  responseId, onClose, onNavigate, footer,
}: {
  responseId: string;
  onClose?: () => void;
  /** Lets the caller drive next/previous from its own list order rather than the form's. */
  onNavigate?: (id: string) => void;
  footer?: React.ReactNode;
}) {
  // Revealing personal data is an audited disclosure, so it is per response and resets
  // when the reader moves on. A sticky "show everything" toggle would turn one deliberate
  // decision into a setting somebody forgets is on.
  const [reveal, setReveal] = useState(false);

  const detail = useQuery({
    queryKey: ['response-detail', responseId, reveal],
    queryFn: () => api.inbox.detail(responseId, reveal),
  });

  if (detail.isLoading) {
    return <div className="p-8"><Spinner label="Loading response" /></div>;
  }

  if (detail.error || !detail.data) {
    return (
      <div className="p-8 text-sm text-[var(--ff-text-muted)]">
        This response could not be loaded. It may have been deleted.
      </div>
    );
  }

  const data = detail.data;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Header
        data={data}
        reveal={reveal}
        onReveal={() => setReveal(true)}
        onHide={() => setReveal(false)}
        onClose={onClose}
        onNavigate={onNavigate}
      />

      <div className="min-h-0 flex-1 space-y-5 overflow-auto px-4 py-4">
        {data.pages.map((page, index) => (
          <section key={`${page.ref}-${index}`} className="space-y-3">
            {(page.title || data.pages.length > 1) && (
              <div className="border-b border-[var(--ff-border)] pb-1.5">
                <h3 className="text-xs font-semibold uppercase tracking-wide
                               text-[var(--ff-text-muted)]">
                  {page.title || `Page ${index + 1}`}
                </h3>
                {page.description && (
                  <p className="mt-0.5 text-xs text-[var(--ff-text-muted)]">{page.description}</p>
                )}
              </div>
            )}

            <div className="space-y-3">
              {page.elements.map((element) => (
                <Element key={element.ref} element={element} />
              ))}
            </div>
          </section>
        ))}

        {data.calculated && data.calculated.length > 0 && (
          <Panel
            title="Calculated"
            hint="Worked out by the server at submission and stored with the response, not
                  recomputed now — editing the formula later does not rewrite history."
          >
            <dl className="space-y-2">
              {data.calculated.map((c) => (
                <div key={c.ref} className="flex items-baseline justify-between gap-4">
                  <dt className="font-mono text-[11px] text-[var(--ff-text-muted)]">{c.ref}</dt>
                  <dd className="text-sm tabular-nums">{c.value || '—'}</dd>
                </div>
              ))}
            </dl>
          </Panel>
        )}

        {data.score && (
          <Panel title="Score">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-lg font-semibold tabular-nums">
                {data.score.totalScore} / {data.score.maxScore}
              </span>
              <span className="text-sm text-[var(--ff-text-muted)] tabular-nums">
                {Math.round(data.score.percentage)}%
              </span>
              {data.score.passed != null && (
                <Badge tone={data.score.passed ? 'success' : 'danger'}>
                  {data.score.passed ? 'Passed' : 'Not passed'}
                </Badge>
              )}
              {data.score.outcomeBandKey && <Badge>{data.score.outcomeBandKey}</Badge>}
            </div>
            <p className="mt-2 text-[11px] text-[var(--ff-text-muted)]">
              Computed on the server. The answer key never reached the browser.
            </p>
          </Panel>
        )}

        {data.extras.length > 0 && (
          <Panel
            title="Answers with no question"
            hint="Stored on this response but not on the version it was answered against —
                  usually a question removed in a later draft. Shown rather than dropped, so
                  this screen agrees with the export."
          >
            <dl className="space-y-2">
              {data.extras.map((extra) => (
                <div key={extra.ref}>
                  <dt className="font-mono text-[11px] text-[var(--ff-text-muted)]">
                    {extra.ref}
                  </dt>
                  <dd className="text-sm">{extra.value || '—'}</dd>
                </div>
              ))}
            </dl>
          </Panel>
        )}

        {footer}
      </div>
    </div>
  );
}

function Header({
  data, reveal, onReveal, onHide, onClose, onNavigate,
}: {
  data: ResponseDetail;
  reveal: boolean;
  onReveal: () => void;
  onHide: () => void;
  onClose?: () => void;
  onNavigate?: (id: string) => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copyJson() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access is refused in some contexts. Saying nothing is better than an
      // error for something the reader can still do by hand.
    }
  }

  const answeredAll = data.answered === data.questionCount;

  return (
    <header className="shrink-0 border-b border-[var(--ff-border)] px-4 py-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <span className="tabular-nums">Response #{data.sequenceNumber}</span>
            <Link
              to={`/forms/${data.form.id}/responses`}
              className="max-w-[16rem] truncate font-normal text-[var(--ff-primary)] hover:underline"
              title={data.form.name}
            >
              {data.form.name}
            </Link>
          </h2>

          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5
                        text-xs text-[var(--ff-text-muted)]">
            <time dateTime={data.submittedUtc} title={new Date(data.submittedUtc).toLocaleString()}>
              {relative(data.submittedUtc)}
            </time>
            {data.durationMs != null && <span>· took {formatDuration(data.durationMs)}</span>}
            <span>· {data.answered} of {data.questionCount} answered</span>
            {data.form.versionNumber != null && <span>· v{data.form.versionNumber}</span>}
          </p>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          {onNavigate && (
            <>
              <IconButton
                label="Newer response"
                disabled={!data.newerId}
                onClick={() => data.newerId && onNavigate(data.newerId)}
              >
                <Icon name="chevronUp" size={16} />
              </IconButton>
              <IconButton
                label="Older response"
                disabled={!data.olderId}
                onClick={() => data.olderId && onNavigate(data.olderId)}
              >
                <Icon name="chevronDown" size={16} />
              </IconButton>
            </>
          )}
          {onClose && (
            <IconButton label="Close" onClick={onClose}>
              <Icon name="close" size={16} />
            </IconButton>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <Badge tone={data.status === 'complete' ? 'success' : 'warning'}>{data.status}</Badge>
        <Badge>{data.channel}</Badge>
        {!answeredAll && (
          <Badge tone="warning">{data.questionCount - data.answered} unanswered</Badge>
        )}
        {data.identity && (
          <Badge tone="primary">
            <span className="max-w-[12rem] truncate">{data.identity}</span>
          </Badge>
        )}

        <span className="ml-auto flex items-center gap-1">
          {/* Only offered when there is something to reveal and the reader is allowed to
              see it. A disabled control that never enables is just a question nobody can
              answer. */}
          {data.hasPii && data.canRevealPii && (
            <Button
              variant="ghost"
              className="min-h-8 px-2 py-1 text-xs"
              onClick={reveal ? onHide : onReveal}
            >
              <Icon name={reveal ? 'eyeOff' : 'search'} size={14} />
              {reveal ? 'Hide personal data' : 'Reveal personal data'}
            </Button>
          )}

          <Button variant="ghost" className="min-h-8 px-2 py-1 text-xs" onClick={copyJson}>
            <Icon name="copy" size={14} />
            {copied ? 'Copied' : 'Copy JSON'}
          </Button>

          <Button
            variant="ghost"
            className="min-h-8 px-2 py-1 text-xs"
            onClick={() => downloadPdf(data.id)}
          >
            <Icon name="page" size={14} /> PDF
          </Button>
        </span>
      </div>

      {data.hasPii && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-[var(--ff-surface-sunken)]
                      px-2 py-1.5 text-[11px] text-[var(--ff-text-muted)]">
          <Icon name="shield" size={13} className="mt-px shrink-0" />
          {reveal
            ? 'Personal data is shown. This disclosure has been recorded in the audit log.'
            : data.canRevealPii
              ? 'Personal data is hidden. Revealing it is recorded in the audit log.'
              : 'Personal data is hidden. Seeing it needs the response:read_pii permission.'}
        </p>
      )}
    </header>
  );
}

function Element({ element }: { element: AnswerElement }) {
  if (element.kind === 'question') return <Answer node={element} />;

  if (element.repeating) {
    return (
      <div className="rounded-lg border border-[var(--ff-border)]">
        <div className="border-b border-[var(--ff-border)] bg-[var(--ff-surface-sunken)]
                        px-3 py-1.5">
          <h4 className="text-xs font-semibold">{element.title || element.ref}</h4>
          <p className="text-[11px] text-[var(--ff-text-muted)]">
            {(element.entries?.length ?? 0) === 0
              ? 'No entries'
              : `${element.entries!.length} ${element.entries!.length === 1 ? 'entry' : 'entries'}`}
          </p>
        </div>

        {/* Each entry keeps its own block. Flattening them would lose which answer went
            with which — the whole point of a repeating group. */}
        <div className="divide-y divide-[var(--ff-border)]">
          {element.entries?.map((entry, index) => (
            <div key={index} className="space-y-3 px-3 py-3">
              <div className="text-[11px] font-semibold text-[var(--ff-text-muted)]">
                {entry.label}
              </div>
              {entry.elements.map((child) => (
                <Answer key={child.ref} node={child} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-[var(--ff-border)] px-3 py-3">
      {element.title && <h4 className="text-xs font-semibold">{element.title}</h4>}
      {element.elements?.map((child) => <Answer key={child.ref} node={child} />)}
    </div>
  );
}

function Answer({ node }: { node: AnswerNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] sm:gap-4">
      <div className="min-w-0">
        <div className="flex items-start gap-1.5 text-xs font-medium leading-snug">
          <Icon
            name={iconFor(node.type)}
            size={13}
            className="mt-0.5 shrink-0 text-[var(--ff-text-muted)]"
          />
          <span className="min-w-0">{node.title}</span>
        </div>

        {/* The ref is what an export column and a webhook payload are keyed by, so it stays
            visible — just no longer as the only label. */}
        <div className="mt-0.5 pl-[18px] font-mono text-[10px] text-[var(--ff-text-muted)]">
          {node.ref}
          {node.pii && <span className="ml-1 uppercase">· {node.pii}</span>}
        </div>
      </div>

      <div className="min-w-0">
        {!node.answered
          ? <span className="text-sm italic text-[var(--ff-text-muted)]">Not answered</span>
          : <Value node={node} />}
      </div>
    </div>
  );
}

function Value({ node }: { node: AnswerNode }) {
  if (node.masked) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-[var(--ff-surface-sunken)]
                       px-2 py-1 font-mono text-sm">
        <Icon name="shield" size={13} className="text-[var(--ff-text-muted)]" />
        {node.display || '••••••'}
      </span>
    );
  }

  if (node.matrix) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {node.matrix.rows.map((row) => (
              <tr key={row.ref} className="border-b border-[var(--ff-border)] last:border-0">
                <th scope="row" className="py-1 pr-3 text-left font-normal
                                           text-[var(--ff-text-muted)]">
                  {row.label}
                </th>
                <td className="py-1 font-medium">{row.value || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (node.files && node.files.length > 0) {
    return (
      <ul className="space-y-1">
        {node.files.map((file) => <FileRow key={file.id} file={file} />)}
      </ul>
    );
  }

  // Choices are drawn as the full list with the picked ones marked, so the reader sees
  // what was on offer. "No" means something different among two options than among nine.
  if (node.choices && node.choices.length > 0 && node.choices.length <= 12) {
    return (
      <ul className="flex flex-wrap gap-1">
        {node.choices.map((choice) => (
          <li
            key={choice.value}
            className={clsx(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
              choice.selected
                ? 'border-[var(--ff-primary)] bg-[var(--ff-primary)]/10 font-medium text-[var(--ff-primary)]'
                : 'border-[var(--ff-border)] text-[var(--ff-text-muted)]',
            )}
          >
            {/* Selection is marked with a tick as well as colour: colour is never the only
                encoding (docs/17 §6). */}
            {choice.selected && <Icon name="check" size={11} />}
            {choice.label}
          </li>
        ))}

        {/* An "other, please specify" answer is submitted as its own value, so it will not
            match any choice and would otherwise vanish from this list. */}
        {node.display && !node.choices.some((c) => c.selected) && (
          <li className="inline-flex items-center rounded-full border border-[var(--ff-primary)]
                         bg-[var(--ff-primary)]/10 px-2 py-0.5 text-xs font-medium
                         text-[var(--ff-primary)]">
            {node.display}
          </li>
        )}
      </ul>
    );
  }

  if (node.type === 'long_text') {
    return (
      <p className="whitespace-pre-wrap rounded-md bg-[var(--ff-surface-sunken)] px-2.5 py-2
                    text-sm leading-relaxed">
        {node.display}
      </p>
    );
  }

  if (node.type === 'signature' && typeof node.value === 'string' &&
      node.value.startsWith('data:image/')) {
    return (
      <img
        src={node.value}
        alt="Signature as drawn by the respondent"
        className="max-h-24 rounded-md border border-[var(--ff-border)] bg-white p-1"
      />
    );
  }

  if (NUMERIC_TYPES.has(node.type ?? '')) {
    return <span className="text-sm font-semibold tabular-nums">{node.display}</span>;
  }

  return <span className="text-sm">{node.display || '—'}</span>;
}

function FileRow({ file }: { file: ResponseFileRef }) {
  const [busy, setBusy] = useState(false);

  return (
    <li className="flex items-center gap-2 text-sm">
      <Icon name="upload" size={14} className="shrink-0 text-[var(--ff-text-muted)]" />

      <button
        type="button"
        disabled={busy || file.scanStatus === 'infected'}
        onClick={async () => {
          setBusy(true);
          try { await downloadFile(file); } finally { setBusy(false); }
        }}
        className="min-w-0 truncate text-left text-[var(--ff-primary)] hover:underline
                   disabled:text-[var(--ff-text-muted)] disabled:no-underline"
      >
        {file.filename}
      </button>

      <span className="shrink-0 text-[11px] text-[var(--ff-text-muted)]">
        {formatBytes(file.sizeBytes)}
      </span>

      {/* A file that failed or has not finished scanning is still listed, and says so. An
          upload silently missing from the record is worse than one marked unsafe. */}
      {file.scanStatus !== 'clean' && (
        <Badge tone={file.scanStatus === 'infected' ? 'danger' : 'warning'}>
          {file.scanStatus}
        </Badge>
      )}
    </li>
  );
}

function Panel({
  title, hint, children,
}: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-[var(--ff-border)] px-3 py-3">
      <h3 className="text-xs font-semibold">{title}</h3>
      {hint && <p className="mb-2 mt-0.5 text-[11px] text-[var(--ff-text-muted)]">{hint}</p>}
      <div className={hint ? '' : 'mt-2'}>{children}</div>
    </section>
  );
}

/* ---- plumbing -------------------------------------------------------------------- */

const NUMERIC_TYPES = new Set([
  'number', 'currency', 'rating', 'nps', 'slider', 'star', 'scale', 'likert', 'emoji_scale',
]);

const TYPE_ICONS: Record<string, IconName> = {
  short_text: 'text', long_text: 'paragraph', email: 'at', phone: 'phone', url: 'link',
  number: 'hash', currency: 'currency', password: 'shield',
  single_select: 'radio', multi_select: 'checkbox', dropdown: 'chevronDown',
  searchable_dropdown: 'search', image_choice: 'image', yes_no: 'toggle',
  likert: 'scale', rating: 'star', nps: 'gauge', slider: 'slider', star: 'star',
  emoji_scale: 'scale', ranking: 'rank', matrix_single: 'grid', matrix_multi: 'gridCheck',
  date: 'calendar', time: 'clock', datetime: 'calendarClock', country: 'globe',
  file_upload: 'upload', signature: 'signature', calculated: 'function',
};

const iconFor = (type?: string | null): IconName =>
  (type && TYPE_ICONS[type]) || 'text';

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Downloads go through fetch rather than a plain link because the endpoints are
 * bearer-authenticated: an anchor sends no Authorization header and lands on a 401.
 */
async function authedDownload(url: string, filename: string) {
  const auth = authStore.read();
  const res = await fetch(url, {
    headers: auth ? { Authorization: `Bearer ${auth.accessToken}` } : {},
  });
  if (!res.ok) return;

  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

const downloadPdf = (responseId: string) =>
  authedDownload(api.inbox.pdfUrl(responseId), `${responseId}.pdf`);

const downloadFile = (file: ResponseFileRef) =>
  authedDownload(file.downloadUrl, file.filename);
